import { Fragment, useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { AMD_COLOR } from "../components/chartTheme";
import { Button, EmptyState, PageBody, PageHeader, Select, StatusPill, TextInput, amdTone } from "../components/ui";
import { ApiError, api, historySearchParams, parseHistory, recordingBasename, request, triggerBlobDownload } from "../lib/api";
import { dash, formatDuration, formatMatch, formatWhen, maskPartyNumber } from "../lib/format";
import {
  HANGUP_CLASS_LABEL,
  AMD_OPERATOR_LABEL,
  HISTORY_AMD,
  HISTORY_OUTCOME_LABEL,
  HISTORY_OUTCOMES,
  hangupCauseClass,
  hangupClassTone,
  historyAmdFromRow,
  historyAmdOperatorLabel,
  historyDecisionLabel,
  historyEnteredCode,
  historyMatch,
  historyOtpState,
  historyOutcome,
  judgmentChipLabel,
  judgmentChipTone,
  outcomeTone,
  recordingEvidence,
  recordingEvidenceTooltip,
  recordingPlayable,
  type HistoryOutcome,
} from "../lib/historyOutcome";
import { useStore } from "../lib/store";
import type { CallRecord, HistoryFilters, OperatorDecisionRow } from "../lib/types";

const HISTORY_LIMIT = 200;
// When Name Dest CLI AMD Trunk Class Outcome Dur+audio Match Judgment Cause
const TABLE_COLS = 12;
const AMD_MIX_KEYS = ["HUMAN", "MACHINE", "SCREENING"] as const;

type AmdFilter = "all" | (typeof HISTORY_AMD)[number];
type MatchFilter = "all" | "yes" | "no";
type OutcomeFilter = "all" | HistoryOutcome;

type OverviewStrip = {
  attempts?: number | null;
  human_asr?: number | null;
  network_asr?: number | null;
  amd_mix?: Record<string, number>;
};

const CSV_COLUMNS = [
  "id",
  "timestamp",
  "customer_name",
  "destination",
  "cli_used",
  "amd_result",
  "call_type",
  "duration_ms",
  "final_match",
  "hangup_cause",
  "outcome",
  "judgment_raw",
  "code",
  "expected_code",
  "operator_decisions",
  "recording_path",
  "recording_bytes",
] as const;

// call_type folds in historyAmdFromRow: pstn_ring and recording_tone are
// SCREENING (E.180 tone is never MACHINE). One option per AMD family.
const AMD_OPTIONS: Array<{ value: AmdFilter; label: string }> = [
  { value: "all", label: "AMD: all" },
  ...HISTORY_AMD.map((value) => ({ value, label: AMD_OPERATOR_LABEL[value] })),
];

const MATCH_OPTIONS: Array<{ value: MatchFilter; label: string }> = [
  { value: "all", label: "Match: all" },
  { value: "yes", label: "yes" },
  { value: "no", label: "no" },
];

const OUTCOME_OPTIONS: Array<{ value: OutcomeFilter; label: string }> = [
  { value: "all", label: "Outcome: all" },
  ...HISTORY_OUTCOMES.map((value) => ({ value, label: HISTORY_OUTCOME_LABEL[value] })),
];

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = Array.isArray(value) ? value.join("|") : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function decisionSummary(rows: OperatorDecisionRow[] | undefined): string {
  return (rows ?? [])
    .map((row) => row.action?.trim())
    .filter(Boolean)
    .join("|");
}

function recordsToCsv(rows: CallRecord[]): string {
  const header = CSV_COLUMNS.join(",");
  const body = rows
    .map((row) =>
      CSV_COLUMNS.map((column) => {
        if (column === "recording_path") return csvEscape(recordingBasename(row.recording_path));
        if (column === "final_match") {
          if (row.final_match === true) return "yes";
          if (row.final_match === false) return "no";
          return "";
        }
        if (column === "code") return csvEscape(historyEnteredCode(row));
        if (column === "operator_decisions") return csvEscape(decisionSummary(row.operator_decisions));
        if (column === "outcome") return csvEscape(historyOutcome(row));
        if (column === "amd_result") return csvEscape(historyAmdFromRow(row) || "");
        if (column === "destination") return csvEscape(maskPartyNumber(row.destination));
        if (column === "cli_used") return csvEscape(maskPartyNumber(row.cli_used));
        return csvEscape(row[column]);
      }).join(","),
    )
    .join("\n");
  // UTF-8 BOM for the Blob fallback so Excel keeps CJK/Cyrillic/Arabic names.
  return `\ufeff${header}\n${body}\n`;
}

function formatAsr(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return "—";
  const n = Number(rate);
  const value = n <= 1 && n >= -1 ? n * 100 : n;
  return `${value.toFixed(1)}%`;
}

function mixCount(mix: Record<string, number> | undefined, key: string): number {
  if (!mix) return 0;
  if (typeof mix[key] === "number" && Number.isFinite(mix[key])) return mix[key];
  const hit = Object.entries(mix).find(([k]) => k.toUpperCase() === key.toUpperCase());
  const n = hit ? Number(hit[1]) : 0;
  return Number.isFinite(n) ? n : 0;
}

function rowAmd(row: CallRecord): string {
  return historyAmdFromRow(row);
}

function rowOutcome(row: CallRecord): HistoryOutcome {
  return historyOutcome(row);
}

function matchesAmd(row: CallRecord, amd: AmdFilter): boolean {
  if (amd === "all") return true;
  return historyAmdFromRow(row) === amd;
}

function matchesMatch(row: CallRecord, match: MatchFilter): boolean {
  if (match === "all") return true;
  const value = historyMatch(row);
  if (match === "yes") return value === true;
  return value === false;
}

function matchesOutcome(row: CallRecord, outcome: OutcomeFilter): boolean {
  if (outcome === "all") return true;
  return rowOutcome(row) === outcome;
}

function matchesQuery(row: CallRecord, q: string): boolean {
  if (!q) return true;
  const blob = [
    row.id,
    row.customer_name,
    row.destination,
    maskPartyNumber(row.destination),
    row.cli_used,
    maskPartyNumber(row.cli_used),
    rowAmd(row),
    row.call_type,
    row.hangup_cause,
    row.trunk_id,
    row.far_end_class,
    rowOutcome(row),
    row.judgment_outcome,
    row.judgment_raw,
    historyEnteredCode(row),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return blob.includes(q);
}

function toFilters(q: string, amd: AmdFilter, match: MatchFilter, outcome: OutcomeFilter): HistoryFilters {
  return {
    q: q.trim() || undefined,
    amd: amd === "all" ? undefined : amd,
    match: match === "all" ? undefined : match,
    outcome: outcome === "all" ? undefined : outcome,
  };
}

function OverviewStats({ data }: { data: OverviewStrip }) {
  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 overflow-visible rounded-2xl border border-border bg-surface px-4 py-2.5 text-sm"
      aria-label="Call stats"
    >
      <span>
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Attempts</span>
        <span className="ml-2 font-mono tabular-nums text-ink">{data.attempts ?? "—"}</span>
      </span>
      <span>
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Human ASR</span>
        <span className="ml-2 font-mono tabular-nums text-ink">{formatAsr(data.human_asr)}</span>
      </span>
      <span>
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Network ASR</span>
        <span className="ml-2 font-mono tabular-nums text-ink">{formatAsr(data.network_asr)}</span>
      </span>
      <span className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">AMD</span>
        {AMD_MIX_KEYS.map((key) => (
          <span key={key} className="inline-flex items-center gap-1 font-mono text-xs tabular-nums text-ink">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: AMD_COLOR[key] }} />
            {key} {mixCount(data.amd_mix, key)}
          </span>
        ))}
      </span>
      <Link to="/stats" className="text-xs text-ink-2 hover:text-ink">
        Stats
      </Link>
    </div>
  );
}

/**
 * Production call log. AMD, match, and outcome hit `/api/history`. Search is client-side on the last 200. CSV prefers `/api/history.csv`.
 */
export function History() {
  const { run, toast } = useStore();
  const [params] = useSearchParams();
  const urlQ = (params.get("q") ?? "").trim();
  const [qInput, setQInput] = useState(urlQ);
  const [amd, setAmd] = useState<AmdFilter>("all");
  const [match, setMatch] = useState<MatchFilter>("all");
  const [outcome, setOutcome] = useState<OutcomeFilter>("all");
  const [records, setRecords] = useState<CallRecord[]>([]);
  const [overview, setOverview] = useState<OverviewStrip | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setQInput(urlQ);
  }, [urlQ]);

  const q = qInput.trim().toLowerCase();
  const filters = useMemo(() => toFilters(qInput, amd, match, outcome), [qInput, amd, match, outcome]);

  const load = useCallback(async () => {
    // q stays client-side: this page is already the last 200, and the table shows masked numbers.
    const raw = await request<unknown>(
      `/api/history?${historySearchParams(HISTORY_LIMIT, {
        amd: filters.amd,
        match: filters.match,
        outcome: filters.outcome,
      })}`,
    );
    setRecords(parseHistory(raw));
  }, [filters.amd, filters.match, filters.outcome]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void run(load, "Could not load history");
    }, 250);
    return () => window.clearTimeout(handle);
  }, [load, run, tick]);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const body = await request<OverviewStrip>("/api/orchestrator/overview", { signal: ac.signal });
        if (!cancelled) setOverview(body && typeof body === "object" ? body : null);
      } catch {
        if (!cancelled) setOverview(null);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [tick]);

  const rows = useMemo(
    () =>
      records.filter(
        (row) =>
          matchesQuery(row, q) && matchesAmd(row, amd) && matchesMatch(row, match) && matchesOutcome(row, outcome),
      ),
    [records, q, amd, match, outcome],
  );

  async function downloadCsv() {
    setBusy(true);
    try {
      const result = await api.historyCsv(HISTORY_LIMIT, filters);
      triggerBlobDownload(result.blob, result.filename);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        const csv = recordsToCsv(rows);
        triggerBlobDownload(new Blob([csv], { type: "text/csv;charset=utf-8" }), "cdialer-history.csv");
        toast("CSV exported from loaded rows", "info");
      } else {
        await run(async () => {
          throw err;
        }, "CSV download failed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function downloadRecording(row: CallRecord, event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    const name = recordingBasename(row.recording_path);
    if (!name) return;
    await run(async () => {
      const result = await api.recording(row.id);
      const filename = result.filename ?? (name.toLowerCase().endsWith(".wav") ? name : `${name}.wav`);
      triggerBlobDownload(result.blob, filename);
    }, "Recording download failed");
  }

  const empty = records.length === 0;
  const filtered = Boolean(q) || amd !== "all" || match !== "all" || outcome !== "all";

  return (
    <PageBody className="flex min-h-0 flex-col overflow-hidden">
      <PageHeader
        kicker="Audit"
        title="History"
        actions={
          <>
            <Button variant="ghost" onClick={() => setTick((n) => n + 1)} disabled={busy}>
              Refresh
            </Button>
            <Button variant="primary" onClick={() => void downloadCsv()} disabled={busy}>
              Download CSV
            </Button>
          </>
        }
      />
      {overview ? <OverviewStats data={overview} /> : null}
      <div className="relative z-30 mb-4 flex shrink-0 flex-wrap items-end gap-3 overflow-visible">
        <div className="min-w-[220px] flex-1">
          <TextInput
            value={qInput}
            onChange={(event) => setQInput(event.target.value)}
            placeholder="Search name, dest, CLI…"
            aria-label="Search call log"
          />
        </div>
        <Select
          value={amd}
          aria-label="AMD filter"
          className="w-auto min-w-[12rem] max-w-[18rem]"
          onChange={(event) => setAmd(event.target.value as AmdFilter)}
        >
          {AMD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Select
          value={match}
          aria-label="Match filter"
          className="w-auto min-w-[12rem] max-w-[18rem]"
          onChange={(event) => setMatch(event.target.value as MatchFilter)}
        >
          {MATCH_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Select
          value={outcome}
          aria-label="Outcome filter"
          className="w-auto min-w-[12rem] max-w-[18rem]"
          onChange={(event) => setOutcome(event.target.value as OutcomeFilter)}
        >
          {OUTCOME_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <span className="font-mono text-sm tabular-nums text-teal">
          {rows.length} / {HISTORY_LIMIT}
        </span>
      </div>
      {empty ? (
        <EmptyState
          title={filtered ? "No matching calls" : "No calls yet"}
          body={
            filtered
              ? "Widen search, AMD, match, or outcome filters. Refresh reloads the last 200 rows."
              : "Completed originates land here. Last 200 rows from SQLite."
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState title="No matching calls" body="Widen search, AMD, match, or outcome filters. Refresh reloads the last 200 rows." />
      ) : (
        <div className="relative z-0 min-h-0 flex-1 overflow-auto rounded-2xl border border-border bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-surface text-[11px] uppercase tracking-[0.16em] text-mute">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Dest</th>
                <th className="px-4 py-3">CLI</th>
                <th className="px-4 py-3">AMD</th>
                <th className="px-4 py-3">Trunk</th>
                <th className="px-4 py-3">Class</th>
                <th className="px-4 py-3">Outcome</th>
                <th className="px-4 py-3">Dur</th>
                <th className="px-4 py-3">Match</th>
                <th className="px-4 py-3" title="Expected-code match judgment — not the call outcome">
                  Judgment
                </th>
                <th className="px-4 py-3" title="Who ended the call: engine, network, or operator">
                  Cause
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const open = expanded === row.id;
                const recName = recordingBasename(row.recording_path);
                const amdLabel = rowAmd(row);
                const outcomeLabel = rowOutcome(row);
                const matchValue = historyMatch(row);
                // Judgment = expected-code match, NOT the call outcome. Rendered
                // as a neutral chip; never red even when outcome=answered-human.
                const judgeChip = judgmentChipLabel(row);
                const causeClass = hangupCauseClass(row.hangup_cause);
                const evidence = recordingEvidence(row);
                const evidenceTip = recordingEvidenceTooltip(row);
                const playable = recordingPlayable(row);
                const entered = historyEnteredCode(row);
                const decisionLines = (row.operator_decisions ?? [])
                  .map((decision) => historyDecisionLabel(decision))
                  .filter(Boolean);
                return (
                  <Fragment key={row.id}>
                    <tr
                      className="cursor-pointer border-t border-teal/8 hover:bg-white/3"
                      onClick={() => setExpanded(open ? null : row.id)}
                      aria-expanded={open}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-mute">
                        <span className="mr-2 text-teal/50">{open ? "▾" : "▸"}</span>
                        {formatWhen(row.timestamp)}
                      </td>
                      <td className="px-4 py-2.5">{dash(row.customer_name)}</td>
                      <td className="px-4 py-2.5 font-mono">{maskPartyNumber(row.destination)}</td>
                      <td className="px-4 py-2.5 font-mono text-mute">{maskPartyNumber(row.cli_used)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <StatusPill tone={amdTone(amdLabel)}>{dash(historyAmdOperatorLabel(row))}</StatusPill>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-mute">{dash(row.trunk_id)}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{dash(row.far_end_class)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <StatusPill tone={outcomeTone(outcomeLabel)}>{outcomeLabel}</StatusPill>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 font-mono tabular-nums">
                        {formatDuration(row.duration_ms)}
                        {evidence === "missing" ? (
                          <span className="ml-2 align-middle" title={evidenceTip || "No audio evidence stored"}>
                            <StatusPill tone="mute">no audio</StatusPill>
                          </span>
                        ) : evidence === "truncated" ? (
                          <span className="ml-2 align-middle" title={evidenceTip}>
                            <StatusPill tone="live">audio truncated</StatusPill>
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <StatusPill tone={matchValue === true ? "ok" : matchValue === false ? "fail" : "mute"}>
                          {formatMatch(matchValue)}
                        </StatusPill>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        {judgeChip ? (
                          <span
                            title="Expected-code match judgment from the CDR — not the call outcome. Operator Accept stays authoritative."
                            className="font-mono text-xs text-mute"
                          >
                            <StatusPill tone={judgmentChipTone(row)}>{judgeChip}</StatusPill>
                          </span>
                        ) : (
                          <span className="text-mute">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span
                          className="font-mono text-xs"
                          title={
                            causeClass === "engine"
                              ? "Engine-decided hangup (screening guard / machine)"
                              : causeClass === "operator"
                                ? "Operator ended the call"
                                : causeClass === "network"
                                  ? "Network / remote end dropped the call"
                                  : "Hangup cause"
                          }
                        >
                          <StatusPill tone={hangupClassTone(causeClass)}>
                            {dash(row.hangup_cause)}
                            {causeClass !== "other" ? ` · ${HANGUP_CLASS_LABEL[causeClass]}` : ""}
                          </StatusPill>
                        </span>
                      </td>
                    </tr>
                    {open ? (
                      <tr className="border-t border-teal/8 bg-black/20">
                        <td colSpan={TABLE_COLS} className="px-4 py-3">
                          <div className="grid gap-4 md:grid-cols-3">
                            <div>
                              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
                                Code
                              </div>
                              <div className="font-mono text-sm text-ink">Code: {entered || "—"}</div>
                              <div className="font-mono text-xs text-mute">
                                expected {dash(row.expected_code ?? null)}
                              </div>
                              {decisionLines.length ? (
                                <ul className="mt-2 space-y-1 font-mono text-xs text-mute">
                                  {decisionLines.map((label, index) => (
                                    <li key={`${row.id}-o-${index}`}>{label}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                            <div>
                              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
                                Result
                              </div>
                              <div className="space-y-1 font-mono text-xs text-ink">
                                <div>otp {dash(historyOtpState(row) || row.otp_state)}</div>
                                <div className="text-mute" title="Expected-code match judgment — not the call outcome">
                                  {dash(judgeChip)}
                                </div>
                                <div>phase {dash(row.call_type)}</div>
                                <div>
                                  cause {dash(row.hangup_cause)}
                                  {causeClass !== "other" ? (
                                    <span className="text-mute"> · {HANGUP_CLASS_LABEL[causeClass]}</span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
                                Recording
                              </div>
                              {recName && playable ? (
                                <div className="space-y-1.5">
                                  <Button
                                    variant="ghost"
                                    className="text-xs"
                                    onClick={(event) => void downloadRecording(row, event)}
                                  >
                                    Download {recName}
                                  </Button>
                                  {evidence === "truncated" ? (
                                    <div className="font-mono text-xs text-live" title={evidenceTip}>
                                      ⚠ audio truncated — file shorter than call duration
                                    </div>
                                  ) : null}
                                </div>
                              ) : (
                                <div className="font-mono text-xs text-mute" title={evidenceTip || undefined}>
                                  No audio evidence{recName ? " (file missing)" : ""}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageBody>
  );
}
